import React, { useEffect, useMemo, useRef } from "react";
import { Stack } from "react-bootstrap";
import { colors } from "src/styles";
import styled from "styled-components";
import * as d3 from "d3";

interface WidgetProps {
  caption: string;
}

interface RegionWorkData {
  region: string;
  percentage: number;
  color: string;
}

const OpenWorkByRegionChart = ({ caption }: WidgetProps) => {
  const svgRef = useRef<SVGSVGElement>(null);

  const regionWorkData: RegionWorkData[] = useMemo(
    () => [
      { region: "Toronto", percentage: 24, color: "#4F81BD" },
      { region: "Vancouver", percentage: 18, color: "#70AD47" },
      { region: "Saskatchewan", percentage: 16, color: "#F4B183" },
      { region: "Winnipeg", percentage: 22, color: "#9DC3E6" },
      // { region: "", percentage: 12, color: "#C5E0B4" },
      { region: "Edmonton", percentage: 8, color: "#FFD966" },
    ],
    [],
  );

  useEffect(() => {
    if (!svgRef.current || regionWorkData.length === 0) return;

    const width = 260;
    const height = 260;
    const radius = Math.min(width, height) / 2;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [-width / 2, -height / 2, width, height])
      .attr("style", "max-width: 100%; height: auto;");

    const pie = d3
      .pie<RegionWorkData>()
      .value((d) => d.percentage)
      .sort(null);

    const arc = d3
      .arc<d3.PieArcDatum<RegionWorkData>>()
      .innerRadius(0)
      .outerRadius(radius - 16);

    const labelArc = d3
      .arc<d3.PieArcDatum<RegionWorkData>>()
      .innerRadius(radius - 28)
      .outerRadius(radius - 28);

    const arcs = svg.selectAll(".arc").data(pie(regionWorkData)).enter().append("g").attr("class", "arc");

    arcs
      .append("path")
      .attr("fill", (d) => d.data.color)
      .attr("stroke", colors.white.default)
      .attr("stroke-width", 2)
      .style("opacity", 0.9)
      .on("mouseover", function () {
        d3.select(this).style("opacity", 1).style("cursor", "pointer");
      })
      .on("mouseout", function () {
        d3.select(this).style("opacity", 0.9);
      })
      .transition()
      .duration(800)
      .ease(d3.easeCubicOut)
      .attrTween("d", (d) => {
        const interpolate = d3.interpolate({ startAngle: d.startAngle, endAngle: d.startAngle }, d);
        return (t) => arc(interpolate(t)) as string;
      });

    arcs
      .append("text")
      .attr("transform", (d) => `translate(${labelArc.centroid(d)})`)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .attr("font-weight", "600")
      .attr("fill", colors.white.default)
      .style("text-shadow", "1px 1px 2px rgba(0,0,0,0.5)")
      .style("opacity", 0)
      .text((d) => `${d.data.percentage}%`)
      .transition()
      .delay(750)
      .duration(250)
      .style("opacity", 1);
  }, [regionWorkData]);

  return (
    <WidgetContainer>
      <Stack gap={3}>
        <HeaderRow>
          <h1 style={{ fontWeight: 600, margin: 0 }}>{caption}</h1>
          <StaticDataBadge>Static Data</StaticDataBadge>
        </HeaderRow>

        <ChartContainer>
          <svg ref={svgRef}></svg>
          <Legend>
            {regionWorkData.map((item) => (
              <LegendItem key={item.region}>
                <ColorDot $color={item.color} />
                <LegendText>
                  {item.region}: <strong>{item.percentage}%</strong>
                </LegendText>
              </LegendItem>
            ))}
          </Legend>
        </ChartContainer>
      </Stack>
    </WidgetContainer>
  );
};

export default OpenWorkByRegionChart;

const WidgetContainer = styled.div`
  display: flex;
  border: 1px solid ${colors.grey.light11};
  border-top: 4px solid ${colors.kleenway.green};
  padding: 16px;
  border-radius: 6px;
`;

const ChartContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
`;

const ColorDot = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background-color: ${({ $color }) => $color};
`;

const LegendText = styled.span`
  color: ${colors.grey.dark};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const StaticDataBadge = styled.span`
  background-color: ${colors.grey.light11};
  color: ${colors.grey.dark};
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;
